import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { numberToWords } from "./numberToWords";
import { getOAuthHeaders } from "../constants/oAuthValidation";
import printController from "../lib/printer/printController";
import CWKOTPrinter from "../lib/printer/CWKotPrinter";
import updateOfdStatus from "./updateOfdStatus";
import { salesHistoryDetails } from "../services";
import validityCheck from "./validityCheck";
import { hasPrintedLocal, markPrintedLocal } from "./printGuard";

const getSalesHistoryDetails = async (order_id, selectedOrderStatus, forPrinting = false, channelId, manualprint = false) => {
  // Common variables
  const serverUrl = process.env.REACT_APP_serverUrl;
  const disableAggregatorInvoicePrint = process.env.REACT_APP_DISABLE_AGGREGATOR_INVOICE_PRINT === "true";
  const disableAggregatorKOTPrint = process.env.REACT_APP_DISABLE_AGGREGATOR_KOT_PRINT === "true";
  const authHeaders = getOAuthHeaders();
  const userData = JSON.parse(localStorage.getItem("userData"));
  const tillData = JSON.parse(localStorage.getItem("tillData"));
  const tillValue = JSON.parse(localStorage.getItem("tillValue"));
  const precision = tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2;

  let transformedOrder = null;

  try {
    console.log(`Fetching sales history for order_id: ${order_id}, status: ${selectedOrderStatus}, forPrinting: ${forPrinting}`);

    // Validate inputs
    if (!order_id) {
      console.error("Invalid order_id: ", order_id);
      return null;
    }
    if (!serverUrl || !authHeaders.access_token) {
      console.error("Missing serverUrl or authHeaders: ", { serverUrl, authHeaders });
      return null;
    }

    const data = {
      query: `query {salesHistoryDetails(
                    orderId: "${order_id}"
                  ) {
                    sOrderID
                    created
                    createdby
                    updated
                    updatedby
                    deliveryDate
                    documentno
                    dateordered
                    cwrProductQty
                    orderTime
                    taxamt
                    grosstotal
                    discAmount
                    layAway
                    isReturn
                    paid
                    print
                    csUser {
                      user
                    }
                    csBUnit {
                      csBunitId
                      name
                    }
                    csbUnitLocation {
                      fulladdress
                    }
                    cwrB2cCustomer {
                      cwrCustomerId
                      code
                      name
                      mobileNo
                      pincode
                      email
                      retlLoyaltyBalance
                      sCustomer {
                        sCustomerID
                        customerCategory {
                          sCustomerCateforyId
                          value
                          name
                          description
                        }
                      }
                    }
                    saleType {
                      cwrSaletypeId
                      name
                      value
                      enableKot
                    }
                    cwrTill {
                      cwrTillID
                      till
                    }
                    tablename
                    fboRder {
                      guests
                    }
                    saLesRep {
                      waitername
                    }
                    finReceiptPlan {
                      finReceiptPlanDetails {
                        amount
                        transactionId
                        cardType
                        cardNo
                        transactionTime
                        authorizationCode
                        cwrPaymentmethod {
                          cWRPaymentMethodID
                          finFinancialAccountId
                          finPaymentmethodId
                          integratedPayment
                          isloyalty
                          paymentProvider
                        }
                      }
                    }
                    line {
                      sOrderlineID
                      sOrderId
                      line
                      description
                      qty
                      deliveryDate
                      netlist
                      netunit
                      created
                      linetax
                      unittax
                      unitprice
                      linenet
                      linegross
                      grosslist
                      grossstd
                      returnline
                      returnQty
                      discount
                      mBatchId
                      product {
                        mProductId
                        name
                        value
                        upc
                        hsn
                        hsncode
                        imageurl
                        isManualQty
                        shortDescription
                        returnable
                        returnDays
                        productSegment
                      }
                      uom {
                        csUomId
                        name
                        decimal
                      }
                      tax {
                        csTaxID
                        name
                        rate
                      }
                      pricingRule {
                        mPricingrulesId
                        name
                      }
                      addons {
                        qty
                        price
                        name
                      }
                    }
                    metaData { key value }
                    externalId
                  }
                }`,
    };

    const response = await salesHistoryDetails(data);

    const orderData = response?.data?.data?.salesHistoryDetails?.[0];
    console.log("GraphQL response:", response?.data);

    if (!orderData) {
      console.error(`No order data found for order_id: ${order_id}`);
      return null;
    }

    if (forPrinting || manualprint) {
      const itemSummery = {
        decimalItems: { totalQty: 0, totalAmount: 0 },
        nonDecimalItems: { totalQty: 0, totalAmount: 0 },
        itemsQty: 0,
        items: 0,
      };

      const groupedItems = (orderData.line || []).reduce((acc, item) => {
        const key = item.tax?.rate ?? "0";
        acc[key] = acc[key] || [];
        acc[key].push(item);
        return acc;
      }, {});

      const groupedItemsByTaxRate = Object.entries(groupedItems).map(([taxRate, items]) => {
        const totalTaxAmount = items.reduce((sum, item) => {
          const taxValue = item.linetax || 0;
          return sum + parseFloat(taxValue);
        }, 0);

        const totalNetTotal = items.reduce((sum, item) => {
          const netValue = item.linegross || 0;
          return sum + parseFloat(netValue);
        }, 0);

        const totalSgstAmount = parseFloat(totalTaxAmount) / 2;
        const totalCgstAmount = parseFloat(totalTaxAmount) / 2;

        return {
          taxRate,
          totalLinenet: parseFloat((totalNetTotal - totalTaxAmount).toFixed(precision)),
          totalLinetax: parseFloat(totalTaxAmount.toFixed(precision)),
          totalLinegross: parseFloat(totalNetTotal.toFixed(precision)),
          totalSgst: parseFloat(totalSgstAmount.toFixed(precision)),
          totalCgst: parseFloat(totalCgstAmount.toFixed(precision)),
        };
      });

      const lineItems = (orderData.line || []).map((pro) => {
        const isDecimal = pro.uom?.decimal === "Y" || false;
        const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
        itemSummery.itemsQty += pro.qty;
        itemSummery.items += 1;
        itemSummery[summaryKey].totalQty += pro.qty;
        itemSummery[summaryKey].totalAmount += pro.linegross;

        return {
          batchno: null,
          description: pro.description || "",
          discount: pro.discount ?? 0,
          discountName: pro.pricingRule?.name || "",
          imageurl: pro.product?.imageurl || "",
          isDecimal: isDecimal,
          isManualQty: pro.product?.isManualQty || false,
          deliveryDate: pro.deliveryDate ? moment(pro.deliveryDate).format("DD-MM-YYYY HH:mm:ss") : null,
          isReturn: false,
          mBatchId: pro?.mBatchId || null,
          giftCardType: null,
          mPricingruleId: pro.pricingRule?.mPricingrulesId || null,
          name: pro.product?.name || "",
          name2: "",
          linegross: pro?.linegross || "",
          nettotal: pro.linegross || 0,
          primaryOrderLine: null,
          productId: pro.product?.mProductId || null,
          realPrice: pro.unitprice || 0,
          listPrice: pro.unitprice || 0,
          sunitprice: pro.linenet || 0,
          returnQty: null,
          originalPrice: pro.unitprice || 0,
          productSegment: "",
          salePrice: pro.unitprice || 0,
          stock: 0,
          tax: pro.tax?.csTaxID || null,
          taxCategory: "",
          taxName: pro.tax?.name || "",
          taxFlag: "N",
          taxAmount: pro.linetax || 0,
          taxRate: pro.tax?.rate || 0,
          uom: pro.uom?.csUomId || null,
          uom_name: pro.uom?.name || "",
          addNewLine: "N",
          customAttributes: [],
          showAddons: false,
          foodType: "",
          type: "",
          newCustomAttributes: [],
          priceList: [],
          addons: pro.addons || [],
          isDecimalQty: isDecimal,
          isQtyDesimal: 2,
          upc: pro.product?.upc || null,
          value: pro.product?.value || null,
          weight: pro.qty || 0,
          order: "N",
          productionCenter: "",
          shortDescription: pro.product?.shortDescription || "",
          hsncode: pro.product?.hsn || null,
          csBunitId: null,
          mProductCategoryId: null,
          productManufacturerId: null,
          productBrandId: null,
          productBrandName: null,
          productCategoryName: "",
          productAddons: [],
          batchedProduct: false,
          batchedForSale: false,
          batchedForStock: false,
          multiPrice: [],
          shelfLife: 0,
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          layAway: orderData.layAway || "N",
          grossstd: pro.grossstd || 0,
          linestd: (pro.grossstd || 0) * (pro.qty || 0),
        };
      });

      const paymentMethods = [];
      let change = 0;
      let paidAmount = 0;
      orderData?.finReceiptPlan?.[0]?.finReceiptPlanDetails?.forEach((ele) => {
        if (ele.amount > 0) {
          paymentMethods.push({
            cWRPaymentMethodID: ele.cwrPaymentmethod?.cWRPaymentMethodID || null,
            finFinancialAccountId: ele.cwrPaymentmethod?.finFinancialAccountId || null,
            finPaymentmethodId: ele.cwrPaymentmethod?.finPaymentmethodId || null,
            integratedPayment: ele.cwrPaymentmethod?.integratedPayment || false,
            isloyalty: ele.cwrPaymentmethod?.isloyalty || false,
            paymentProvider: ele.cwrPaymentmethod?.paymentProvider || "Unknown",
            amount: parseFloat(ele.amount.toFixed(2)),
            advancePayment: parseFloat(ele.amount.toFixed(2)),
            transactionId: ele.transactionId || "",
            cardType: ele.cardType || "",
            cardNo: ele.cardNo || "",
            transactionTime: ele.transactionTime ? moment(ele.transactionTime).format("DD-MM-YYYY HH:mm:ss") : "",
            authorizationCode: ele.cwrPaymentmethod?.authorizationCode || "",
          });
          paidAmount += ele.amount;
        } else if (ele.amount < 0) {
          change = ele.amount;
        }
      });
      const totalStd = lineItems?.reduce((sum, item) => sum + (item.linestd || 0), 0);
      transformedOrder = {
        change,
        items: lineItems || [],
        cardPaymentData: {},
        originalPrice: orderData.grosstotal || 0,
        customer: orderData.cwrB2cCustomer || "",
        documentno: orderData.documentno,
        externalId: orderData?.externalId || null,
        total: parseFloat((orderData.grosstotal || 0).toFixed(precision)),
        discount: orderData.discAmount || 0,
        tax: orderData.taxamt || 0,
        deliveryDate: orderData.deliveryDate ? moment(orderData.deliveryDate).format("DD-MM-YYYY HH:mm:ss") : null,
        orderTime: orderData.orderTime ? moment(orderData.orderTime).format("DD-MM-YYYY HH:mm:ss") : null,
        orderDate: orderData.dateordered,
        totalQty: orderData.cwrProductQty,
        payments: [],
        paymentData: paymentMethods || [],
        roundOff: 0,
        sOrderID: orderData.sOrderID,
        key: orderData.documentno,
        salesHistory: "Y",
        status: "Success",
        isSynced: 0,
        creditAmount: 0,
        createdBy: orderData.createdby || "",
        layAway: orderData.layAway || "N",
        isReturn: orderData.isReturn || "N",
        paid: paidAmount || 0,
        advancePayment: paidAmount || 0,
        redemptionPoints: 0,
        accumulationPoints: 0,
        saleType: orderData.saleType || {},
        channel: orderData.saleType?.name || "Unknown",
        metaData: orderData?.metaData,
        quickOrder: (orderData?.metaData?.find((md) => md.key === "Instant Order")?.value === "true" && true) || null,
        orderOTP: orderData?.metaData?.find((md) => md.key === "Order OTP")?.value || null,
        orderInstructions: orderData?.metaData?.find((md) => md.key === "Instructions")?.value || null,
        channelId: `${orderData?.externalId?.slice(-4)}`,
        referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
        userName: userData.user,
        tillName: tillValue?.name,
        subTotal: parseFloat((orderData.grosstotal - orderData.discAmount - orderData.taxamt || 0).toFixed(precision)),
        itemsSummery: itemSummery,
        amountInWords: numberToWords(orderData.grosstotal || 0),
        groupedItemsByTaxRate: groupedItemsByTaxRate,
        totalStd: parseFloat(totalStd.toFixed(precision)),
      };

      // Call CWKOTPrinter if forPrinting is true and transformedOrder is not null
      if (transformedOrder && !manualprint) {
        console.log("Calling CWKOTPrinter with order details:", transformedOrder);
        try {
          const isServerPrinted = orderData?.print === "Y";
          const isLocalPrinted = await hasPrintedLocal(order_id);

          if (isServerPrinted || isLocalPrinted) {
            console.warn(`[PrintGuard] Duplicate print prevented. Server: ${isServerPrinted}, Local: ${isLocalPrinted} for Order: ${order_id}`);
            return;
          }

          console.log(`[PrintGuard] Printing Allowed. Marking local immediately. Order: ${order_id}`);
          await markPrintedLocal(order_id);

          // Perform validity check before printing
          if (!validityCheck(orderData, order_id)) {
            console.log(`Printing skipped for order ${order_id} due to validity check failure`);
            return;
          }
          if (!disableAggregatorInvoicePrint) {
            printController(transformedOrder);
          } else if (!disableAggregatorKOTPrint) {
            CWKOTPrinter(transformedOrder, true);
          }
          console.log(`Successfully printed order ${order_id}`);
          const updateResult = await updateOfdStatus({
            sOrderID: order_id,
            newStatus: selectedOrderStatus,
            printFlag: "Y",
          });
          if (updateResult.success) {
            console.log(`Successfully updated order ${order_id} with print: "N"`);
          } else {
            console.error(`Failed to update order ${order_id} with print: "N"`, updateResult.message);
          }
        } catch (printError) {
          console.error(`Failed to print order ${order_id}:`, printError);
        }
      }
    }

    const paymentMethods =
      orderData.finReceiptPlan?.[0]?.finReceiptPlanDetails?.map((detail) => ({
        name: detail.cwrPaymentmethod.paymentProvider || "Unknown",
        amount: detail.amount,
      })) || [];

    const lineItems = orderData.line.map((item) => ({
      name: item.product.name,
      quantity: item.qty,
      discount: item.discount || "0.00",
      tax: item.linetax || "0.00",
      price: item.linegross,
      linenet: item.linenet,
      addons: item.addons || [],
    }));

    const detailedOrder = {
      sOrderID: orderData.sOrderID,
      orderNo: orderData.documentno,
      channel: orderData.saleType?.name || "Unknown",
      externalId: orderData?.externalId,
      status: selectedOrderStatus || orderData.ofdStatus || "NEW",
      amount: orderData.grosstotal,
      paymentMode: paymentMethods.length > 0 ? paymentMethods[0].name : "Unknown",
      eta: "30 Min",
      customerName: orderData.cwrB2cCustomer?.name || "Unknown",
      customerPhone: orderData.cwrB2cCustomer?.mobileNo || "N/A",
      customerEmail: orderData.cwrB2cCustomer?.email || "N/A",
      customerAddress: orderData.csbUnitLocation?.fulladdress || "N/A",
      items: lineItems,
      orderTime: orderData.orderTime,
      tax: orderData.taxamt,
      discount: orderData.discAmount,
      paid: orderData.paid,
      metaData: orderData?.metaData || null,
      saleType: orderData?.saleType || {},
    };

    return forPrinting ? transformedOrder : manualprint ? { transformedOrder, detailedOrder } : detailedOrder;
  } catch (error) {
    console.error(`Error fetching order details for order_id: ${order_id}`, error);
    return null;
  }
};

export { getSalesHistoryDetails };
